import { useEffect, useState, useCallback } from 'react';
import client from '../api/client';

const chip = (bg, fg) => ({ background: bg, color: fg, padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-block' });
const STATUS_CHIP = {
  ACTIVE:    chip('#dcfce7', '#166534'),
  PAUSED:    chip('#fef9c3', '#854d0e'),
  DRAFT:     chip('#e5e7eb', '#374151'),
  COMPLETED: chip('#dbeafe', '#1e40af'),
  STOPPED:   chip('#fee2e2', '#991b1b'),
};
const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 };
const btn = { padding: '6px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, marginLeft: 8 };
const btnPrimary = { ...btn, background: '#2563eb', color: '#fff', border: '1px solid #2563eb' };
const inp = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 };

export default function SendPlans() {
  const [plans, setPlans] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState({});
  const [openRuns, setOpenRuns] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    name: '', groupId: '', templateId: '',
    startDate: new Date().toISOString().slice(0, 10),
    dailyStartQty: 250, rampIntervalDays: 7, maxDailyQty: 5000, sendHour: 10,
  });

  const load = useCallback(async () => {
    try {
      const r = await client.get('/admin/marketing/send-plans');
      setPlans(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setMsg('Failed to load plans: ' + (e.response?.status || e.message)); }
    try {
      const d = await client.get('/admin/marketing/databases');
      const g = d.data.groups || d.data.savedGroups || d.data.saved_groups || [];
      setGroups(Array.isArray(g) ? g : []);
    } catch (e) { /* group picker degrades to numeric input */ }
    try {
      const t = await client.get('/admin/marketing/templates');
      setTemplates(Array.isArray(t.data) ? t.data : (t.data.templates || []));
    } catch (e) { /* template picker degrades to numeric input */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setMsg('');
    try {
      const r = await client.post(`/admin/marketing/send-plans/${id}/${action}`);
      if (action === 'activate' && r.data && r.data.seededFromPriorCampaigns > 0) {
        setMsg(`Activated. ${r.data.seededFromPriorCampaigns} contacts from earlier campaigns on this group were sealed into the ledger and will never be re-mailed.`);
      }
      load();
    } catch (e) { setMsg(`${action} failed: ` + (e.response?.data?.message || e.response?.status || e.message)); }
  };

  const toggleRuns = async (id) => {
    setOpenRuns(o => ({ ...o, [id]: !o[id] }));
    if (!runs[id]) {
      try {
        const r = await client.get(`/admin/marketing/send-plans/${id}/runs`);
        setRuns(rs => ({ ...rs, [id]: Array.isArray(r.data) ? r.data : [] }));
      } catch (e) { /* leave empty */ }
    }
  };

  const createPlan = async () => {
    setMsg('');
    if (!form.name || !form.groupId || !form.templateId) { setMsg('Name, group, and template are required.'); return; }
    try {
      await client.post('/admin/marketing/send-plans', {
        name: form.name,
        groupId: Number(form.groupId),
        templateId: Number(form.templateId),
        startDate: form.startDate,
        dailyStartQty: Number(form.dailyStartQty),
        rampIntervalDays: Number(form.rampIntervalDays),
        maxDailyQty: Number(form.maxDailyQty),
        sendHour: Number(form.sendHour),
      });
      setShowNew(false);
      setForm(f => ({ ...f, name: '' }));
      load();
    } catch (e) { setMsg('Create failed: ' + (e.response?.data || e.message)); }
  };

  const f = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));
  const projWeeks = () => {
    const g = groups.find(x => String(x.id) === String(form.groupId));
    const total = g ? (g.member_count || g.memberCount || 0) : 0;
    if (!total) return null;
    let sent = 0, day = 0, qty = Number(form.dailyStartQty) || 250;
    const interval = Number(form.rampIntervalDays) || 7, cap = Number(form.maxDailyQty) || 5000;
    while (sent < total && day < 365) {
      day++;
      if (day % 7 === 6 || day % 7 === 0) continue;
      sent += Math.min(cap, qty);
      if (day % interval === 0) qty = Math.min(cap, qty * 2);
    }
    return Math.ceil(day / 7);
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Send plans</h2>
        <div>
          <button style={btn} onClick={load}>Refresh</button>
          <button style={btnPrimary} onClick={() => setShowNew(s => !s)}>{showNew ? 'Close' : 'New plan'}</button>
        </div>
      </div>

      {msg && <div style={{ ...card, background: '#fefce8', border: '1px solid #fde047', fontSize: 13 }}>{msg}</div>}

      {showNew && (
        <div style={card}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>New send plan</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div><span style={lbl}>Plan name</span><input style={inp} value={form.name} onChange={f('name')} placeholder="Insurance TX P&C - V8" /></div>
            <div><span style={lbl}>Audience group</span>
              {groups.length > 0 ? (
                <select style={inp} value={form.groupId} onChange={f('groupId')}>
                  <option value="">— choose —</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.member_count ?? g.memberCount ?? '?'})</option>)}
                </select>
              ) : <input style={inp} value={form.groupId} onChange={f('groupId')} placeholder="group id, e.g. 4" />}
            </div>
            <div><span style={lbl}>Template</span>
              {templates.length > 0 ? (
                <select style={inp} value={form.templateId} onChange={f('templateId')}>
                  <option value="">— choose —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              ) : <input style={inp} value={form.templateId} onChange={f('templateId')} placeholder="template id, e.g. 37" />}
            </div>
            <div><span style={lbl}>Start date</span><input type="date" style={inp} value={form.startDate} onChange={f('startDate')} /></div>
            <div><span style={lbl}>Start at (per day)</span><input type="number" style={inp} value={form.dailyStartQty} onChange={f('dailyStartQty')} /></div>
            <div><span style={lbl}>Double every (days)</span><input type="number" style={inp} value={form.rampIntervalDays} onChange={f('rampIntervalDays')} /></div>
            <div><span style={lbl}>Daily maximum</span><input type="number" style={inp} value={form.maxDailyQty} onChange={f('maxDailyQty')} /></div>
            <div><span style={lbl}>Send hour (CT)</span><input type="number" min="6" max="18" style={inp} value={form.sendHour} onChange={f('sendHour')} /></div>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
            Mon-Fri only. Pauses automatically if complaints exceed 0.08%, bounces 4%, or unsubscribes 6% on any day, then resumes next business day at half rate.
            {projWeeks() && <> Projection: finishes in about <b>{projWeeks()} weeks</b>.</>}
          </div>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button style={btnPrimary} onClick={createPlan}>Create as draft</button>
          </div>
        </div>
      )}

      {plans.length === 0 && !showNew && (
        <div style={{ ...card, textAlign: 'center', color: '#6b7280' }}>
          No send plans yet. Create one, then press Activate when you are ready for it to run.
        </div>
      )}

      {plans.map(p => {
        const total = p.member_count ?? 0;
        const pct = total > 0 ? Math.min(100, Math.round(100 * (p.total_sent ?? 0) / total)) : 0;
        const guardPaused = p.status === 'PAUSED' && String(p.pause_reason || '').startsWith('GUARD');
        return (
          <div key={p.id} style={{ ...card, border: guardPaused ? '1px solid #fbbf24' : card.border }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {p.group_name} · template {p.template_name} · starts {String(p.start_date).slice(0, 10)}
                  {p.last_run_date && <> · last run {String(p.last_run_date).slice(0, 10)}</>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={STATUS_CHIP[p.status] || STATUS_CHIP.DRAFT}>{guardPaused ? 'Paused by guard' : p.status}</span>
                {(p.status === 'DRAFT' || p.status === 'STOPPED') && <button style={btnPrimary} onClick={() => act(p.id, 'activate')}>Activate</button>}
                {p.status === 'ACTIVE' && <button style={btnPrimary} onClick={() => { if (window.confirm('Send today\u2019s batch right now, ignoring the scheduled hour?')) act(p.id, 'run-now'); }}>Send now</button>}
                {p.status === 'ACTIVE' && <button style={btn} onClick={() => act(p.id, 'pause')}>Pause</button>}
                {p.status === 'PAUSED' && !guardPaused && <button style={btn} onClick={() => act(p.id, 'resume')}>Resume</button>}
                {(p.status === 'ACTIVE' || p.status === 'PAUSED') && <button style={{ ...btn, color: '#991b1b' }} onClick={() => { if (window.confirm('Stop this plan permanently? It will not auto-resume.')) act(p.id, 'stop'); }}>Stop plan</button>}
              </div>
            </div>

            {guardPaused && (
              <div style={{ marginTop: 10, background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#854d0e' }}>
                {p.pause_reason} — resumes automatically next business day at half rate. Press Stop plan to prevent that.
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                <span>{(p.total_sent ?? 0).toLocaleString()} of {total.toLocaleString()} sent</span><span>{pct}%</span>
              </div>
              <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: pct + '%', height: '100%', background: '#2563eb' }} />
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <button style={{ ...btn, marginLeft: 0 }} onClick={() => toggleRuns(p.id)}>{openRuns[p.id] ? 'Hide runs' : 'Show runs'}</button>
            </div>

            {openRuns[p.id] && (
              <table style={{ width: '100%', marginTop: 10, fontSize: 13, borderCollapse: 'collapse' }}>
                <thead><tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: 6 }}>Date</th><th>Status</th><th>Planned</th><th>Sent</th><th>Delivered</th><th>Bounced</th><th>Unsub</th><th>Complaints</th><th>Clicks</th><th>Notes</th>
                </tr></thead>
                <tbody>{(runs[p.id] || []).map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 6 }}>{String(r.run_date).slice(0, 10)}</td>
                    <td>{r.status}</td><td>{r.planned_qty}</td><td>{r.sent ?? '-'}</td><td>{r.delivered ?? '-'}</td>
                    <td>{r.bounced ?? '-'}</td><td>{r.unsubscribed ?? '-'}</td><td>{r.complained ?? '-'}</td><td>{r.clicked ?? '-'}</td>
                    <td style={{ color: '#6b7280' }}>{r.notes || ''}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
